import React, { memo, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components';

const contentWidthsBySize = {
  small: '400px',
  normal: '600px',
  large: '800px',
  full: '80vh'
};

const Section = styled('div')`
  padding: 15px 20px;
`;

const ModalHeader = styled(Section)`
  border-bottom: 1px solid #f5f5f5;
  font-size: 15px;
`;

const ModalBody = styled(Section)`
  max-height: 70vh;
  overflow-y: auto;
  padding: 20px;
`;

const ModalFooter = styled(Section)`
  border-top: 1px solid #f5f5f5;
  text-align: left;
`;

const Content = styled('div')`
  background-color: #fff;
  border-radius: 3px;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
  width: ${props => contentWidthsBySize[props.size]};
`;

const ContentWrapper = styled('div')`
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  position: relative;
  z-index: 2;
`;

const Backdrop = styled('div')`
  background-color: rgba(240, 240, 240, 0.75);
  bottom: 0;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 1;
`;

const Wrapper = styled('div')`
  bottom: 0;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 9999;
`;

function Modal(props) {
  const { children, hideOnClickBackdrop, hideOnEscape, onHide, show, size } = props;

  const root = useRef();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    root.current = document.createElement('div');
    document.body.appendChild(root.current);
    setReady(true);

    return () => {
      root.current.parentNode.removeChild(root.current);
    };
  }, []);

  useEffect(() => {
    if (show) {
      // add classname
    }

    if (!show) {
      // remove classname
    }
  }, [show]);

  useEffect(() => {
    const handleKeyDown = event => {
      if (hideOnEscape && event.which === 27 && onHide) {
        onHide();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  });

  const handleClickBackdrop = () => {
    if (hideOnClickBackdrop && onHide) {
      onHide();
    }
  };

  if (show && ready) {
    const content = (
      <Wrapper>
        <Backdrop onClick={handleClickBackdrop} />
        <ContentWrapper>
          <Content size={size}>{children}</Content>
        </ContentWrapper>
      </Wrapper>
    );

    return createPortal(content, root.current);
  }

  return null;
}

Modal.propTypes = {
  hideOnClickBackdrop: PropTypes.bool,
  hideOnEscape: PropTypes.bool,
  onHide: PropTypes.func,
  show: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'normal', 'large', 'full'])
};

Modal.defaultProps = {
  hideOnClickBackdrop: true,
  hideOnEscape: true,
  size: 'normal'
};

export { ModalBody, ModalFooter, ModalHeader };

export default memo(Modal);
